import { useState, useEffect } from 'react';
import { useAuth as useOidcAuth } from 'react-oidc-context';
import { useAuth } from '../hooks/useAuth';
import { apiClient } from '../api/apiClient';

export default function UserDebugPanel() {
  const [isExpanded, setIsExpanded] = useState(false);
  const [abpConfig, setAbpConfig] = useState<any>(null);
  const [abpLoading, setAbpLoading] = useState(false);
  const [abpError, setAbpError] = useState<string | null>(null);
  const oidcAuth = useOidcAuth();
  const auth = useAuth();

  const fetchAbpConfig = async () => {
    if (!oidcAuth.user?.access_token) {
      setAbpError('No access token available');
      return;
    }

    setAbpLoading(true);
    setAbpError(null);

    try {
      const response = await apiClient.get('/api/abp/application-configuration');
      setAbpConfig(response.data);
    } catch (err: any) {
      setAbpError(err.message || 'Failed to fetch ABP configuration');
      console.error('ABP config error:', err);
    } finally {
      setAbpLoading(false);
    }
  };

  useEffect(() => {
    if (oidcAuth.isAuthenticated && isExpanded) {
      fetchAbpConfig();
    }
  }, [oidcAuth.isAuthenticated, isExpanded]);

  // Only show in development
  if (import.meta.env.PROD) {
    return null;
  }

  if (!oidcAuth.isAuthenticated) {
    return (
      <div className="bg-gray-800 text-white px-4 py-2">
        <div className="container-app">
          <p className="text-sm">
            <span className="font-semibold">Debug:</span> Not authenticated
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-800 text-white">
      <div className="container-app">
        {/* Header - Always visible */}
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full flex justify-between items-center px-4 py-2 hover:bg-gray-700 transition-colors"
        >
          <div className="flex items-center space-x-4 text-sm">
            <span className="font-semibold">Debug Panel</span>
            <span className="text-gray-400">User: {auth.user?.email || 'Unknown'}</span>
            <span className="text-gray-400">
              Roles: {auth.roles.length > 0 ? auth.roles.join(', ') : 'None'}
            </span>
          </div>
          <svg
            className={`h-5 w-5 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 15l7-7 7 7"
            />
          </svg>
        </button>

        {/* Expanded content */}
        {isExpanded && (
          <div className="px-4 pb-4 space-y-4">
            {/* User Profile */}
            <div>
              <h3 className="text-sm font-semibold mb-2 text-green-400">User Profile</h3>
              <div className="bg-gray-900 rounded p-3 text-xs font-mono overflow-x-auto">
                <pre>{JSON.stringify(oidcAuth.user?.profile, null, 2)}</pre>
              </div>
            </div>

            {/* Roles */}
            <div>
              <h3 className="text-sm font-semibold mb-2 text-primary-400">Roles</h3>
              <div className="bg-gray-900 rounded p-3">
                {auth.roles.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {auth.roles.map((role) => (
                      <span
                        key={role}
                        className="px-2 py-1 bg-primary-600 rounded text-xs font-medium"
                      >
                        {role}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-gray-400">No roles assigned</p>
                )}
              </div>
            </div>

            {/* Permissions */}
            <div>
              <h3 className="text-sm font-semibold mb-2 text-secondary-400">Permissions</h3>
              <div className="bg-gray-900 rounded p-3">
                {auth.permissions.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {auth.permissions.map((permission) => (
                      <span
                        key={permission}
                        className="px-2 py-1 bg-secondary-600 rounded text-xs font-medium"
                      >
                        {permission}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-gray-400">No permissions</p>
                )}
              </div>
            </div>

            {/* Role Hierarchy Level */}
            <div>
              <h3 className="text-sm font-semibold mb-2 text-yellow-400">Role Hierarchy</h3>
              <div className="bg-gray-900 rounded p-3">
                <p className="text-sm">
                  Highest Role Level: <span className="font-bold">{auth.highestRoleLevel}</span>
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  (7=Admin, 6=OpsAdmin, 5=Publisher, 4=Bookseller, 3=Professor, 2=Student,
                  1=Support)
                </p>
              </div>
            </div>

            {/* Access Token Info */}
            <div>
              <h3 className="text-sm font-semibold mb-2 text-red-400">Access Token Info</h3>
              <div className="bg-gray-900 rounded p-3 text-xs">
                <p>
                  <span className="text-gray-400">Expires At:</span>{' '}
                  {oidcAuth.user?.expires_at
                    ? new Date(oidcAuth.user.expires_at * 1000).toLocaleString()
                    : 'N/A'}
                </p>
                <p>
                  <span className="text-gray-400">Token Type:</span>{' '}
                  {oidcAuth.user?.token_type || 'N/A'}
                </p>
                <p>
                  <span className="text-gray-400">Scopes:</span> {oidcAuth.user?.scope || 'N/A'}
                </p>
              </div>
            </div>

            {/* Full OIDC User Object */}
            <div>
              <h3 className="text-sm font-semibold mb-2 text-orange-400">
                Full OIDC User Object
              </h3>
              <div className="bg-gray-900 rounded p-3 text-xs font-mono overflow-x-auto max-h-96 overflow-y-auto">
                <pre>{JSON.stringify(oidcAuth.user, null, 2)}</pre>
              </div>
            </div>

            {/* ABP Configuration Checker */}
            <div className="border-t border-gray-700 pt-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-cyan-400">ABP Configuration</h3>
                <button
                  onClick={fetchAbpConfig}
                  disabled={abpLoading}
                  className="px-3 py-1 bg-cyan-600 hover:bg-cyan-700 rounded text-xs font-medium disabled:bg-gray-600 transition-colors"
                >
                  {abpLoading ? 'Loading...' : 'Refresh'}
                </button>
              </div>

              {abpError && (
                <div className="bg-red-900/50 border border-red-700 text-red-200 p-3 rounded mb-3">
                  <p className="font-semibold text-sm">Error:</p>
                  <p className="text-xs mt-1">{abpError}</p>
                </div>
              )}

              {abpConfig && (
                <div className="space-y-3">
                  {/* ABP Current User */}
                  {abpConfig.currentUser && (
                    <div>
                      <h4 className="text-xs font-semibold mb-2 text-cyan-300">Current User (ABP)</h4>
                      <div className="bg-gray-900 rounded p-3 text-xs font-mono overflow-x-auto">
                        <pre>{JSON.stringify(abpConfig.currentUser, null, 2)}</pre>
                      </div>
                    </div>
                  )}

                  {/* ABP Granted Policies */}
                  {abpConfig.auth && (
                    <div>
                      <h4 className="text-xs font-semibold mb-2 text-green-300">
                        ABP Granted Policies (Permissions)
                      </h4>
                      {abpConfig.auth.grantedPolicies ? (
                        <div className="bg-gray-900 rounded p-3">
                          <div className="flex flex-wrap gap-2">
                            {Object.entries(abpConfig.auth.grantedPolicies)
                              .filter(([_, granted]) => granted === true)
                              .map(([policy]) => (
                                <span
                                  key={policy}
                                  className="px-2 py-1 bg-green-600 rounded text-xs font-medium"
                                >
                                  {policy}
                                </span>
                              ))}
                          </div>
                          {Object.keys(abpConfig.auth.grantedPolicies).length === 0 && (
                            <p className="text-xs text-gray-400">No permissions granted</p>
                          )}
                        </div>
                      ) : (
                        <p className="text-xs text-gray-400">No grantedPolicies found</p>
                      )}
                    </div>
                  )}

                  {/* Full ABP Config */}
                  <div>
                    <h4 className="text-xs font-semibold mb-2 text-yellow-300">
                      Full ABP Configuration
                    </h4>
                    <div className="bg-gray-900 rounded p-3 text-xs font-mono overflow-x-auto max-h-96 overflow-y-auto">
                      <pre>{JSON.stringify(abpConfig, null, 2)}</pre>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
